<template>
  <div
    class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="h-48 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- SECTION 1: LIVE EDITORIAL PREVIEW (COLLAPSIBLE ON MOBILE) -->
        <section class="space-y-3">
          <div class="flex items-center justify-between px-1">
            <div class="flex items-center gap-2.5">
              <span class="relative flex h-2.5 w-2.5">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"
                ></span>
              </span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5"
              >
                <EyeIcon class="h-4 w-4 text-indigo-600" />
                Live Preview
              </h3>
            </div>

            <div class="flex items-center gap-2">
              <span
                class="hidden sm:inline-flex rounded-lg bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 font-mono text-[10px] font-bold text-indigo-700"
              >
                /about
              </span>
              <span
                class="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700"
              >
                Realtime Mirror
              </span>
            </div>
          </div>

          <!-- Editorial Frame Simulation -->
          <div
            class="rounded-3xl border border-slate-200/90 bg-[#FAF7F1] p-5 sm:p-8 lg:p-10 shadow-sm relative space-y-6 sm:space-y-8"
          >
            <div
              class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center"
            >
              <!-- Left Copy & Report Sheet -->
              <div class="lg:col-span-7 space-y-3 sm:space-y-4">
                <div class="inline-flex items-center gap-2">
                  <span class="w-4 h-[1.5px] bg-[#B8863B]"></span>
                  <span
                    class="font-mono text-[10px] font-bold tracking-widest text-[#1F3A5F] uppercase"
                  >
                    {{ formData.aboutEyebrow || "OUR PURPOSE" }}
                  </span>
                  <span class="w-4 h-[1.5px] bg-[#B8863B]"></span>
                </div>

                <h2
                  class="text-xl sm:text-3xl font-serif font-bold text-[#11151F] tracking-tight leading-snug"
                >
                  {{ formData.aboutHeading || "School of Excellence" }}
                </h2>

                <div class="w-10 h-0.5 bg-[#B8863B]"></div>

                <p
                  class="text-xs sm:text-sm text-[#4B5262] leading-relaxed line-clamp-3 sm:line-clamp-none"
                >
                  {{
                    formData.aboutSchool ||
                    "Our institution operates under the highest standards of holistic education, preparing students for lifetime leadership and character excellence."
                  }}
                </p>

                <!-- Report Sheet Simulation -->
                <div
                  class="rounded-xl border border-[#E1D8C6] bg-white overflow-hidden shadow-xs mt-2"
                >
                  <div
                    class="flex items-center justify-between bg-[#11151F] px-3.5 py-2 sm:px-4 sm:py-2.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#D8B372]"
                  >
                    <span>Report Card</span>
                    <span>{{
                      formData.admissionSession
                        ? `Session ${formData.admissionSession}`
                        : "Session 2026–27"
                    }}</span>
                  </div>

                  <div class="p-3 divide-y divide-[#F1EADC]">
                    <div
                      v-for="(st, idx) in formData.stats"
                      :key="st._id || idx"
                      class="flex items-center justify-between py-1.5 sm:py-2 text-xs"
                    >
                      <span
                        class="font-medium text-[#11151F] text-[11px] sm:text-xs"
                        >{{ st.label || "Metric" }}</span
                      >
                      <span
                        class="font-mono font-bold text-[#1F3A5F] border border-[#E1D8C6] bg-[#FAF7F1] px-2 py-0.5 rounded text-[10px] sm:text-[11px]"
                      >
                        {{ st.number || "0+" }}
                      </span>
                    </div>

                    <div
                      v-if="formData.stats.length === 0"
                      class="py-3 text-center text-[11px] text-slate-400 italic"
                    >
                      No metrics added yet.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Photo Simulation -->
              <div
                class="lg:col-span-5 relative flex justify-center order-first lg:order-last"
              >
                <div
                  class="w-full max-w-sm rounded-2xl overflow-hidden border border-[#E1D8C6] bg-slate-100 aspect-[4/3] shadow-sm relative"
                >
                  <img
                    v-if="
                      photoPreview || formData.aboutImage || formData.heroImage
                    "
                    :src="
                      photoPreview || formData.aboutImage || formData.heroImage
                    "
                    alt="About Institution Photo"
                    class="w-full h-full object-cover"
                  />
                  <div
                    v-else
                    class="w-full h-full flex flex-col items-center justify-center text-slate-400 p-4 text-center"
                  >
                    <ImageIcon class="h-8 w-8 text-slate-300 mb-1" />
                    <span
                      class="text-[10px] font-mono uppercase font-bold text-slate-400"
                      >No Image Selected</span
                    >
                  </div>

                  <!-- Round Stamp Badge -->
                  <div
                    class="absolute -bottom-2 -left-2 sm:-bottom-3 sm:-left-3 h-18 w-18 sm:h-20 sm:w-20 rounded-full bg-[#FAF7F1] border border-[#B8863B] shadow-md flex items-center justify-center text-center font-serif text-[8.5px] sm:text-[9.5px] font-bold leading-tight text-[#11151F] p-1 rotate-[-6deg]"
                  >
                    <span
                      >EST.<br />{{ formData.establishmentYear || "1985"
                      }}<br />EXCELLENCE</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Mini Gallery Preview -->
            <div
              v-if="formData.images && formData.images.length > 0"
              class="pt-5 border-t border-[#E1D8C6]"
            >
              <span
                class="font-mono text-[10px] font-bold text-[#1F3A5F] uppercase tracking-wider block mb-2.5"
              >
                Campus Gallery ({{ formData.images.length }})
              </span>
              <div class="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                <div
                  v-for="(imgSrc, gIdx) in formData.images.slice(0, 6)"
                  :key="gIdx"
                  class="rounded-xl overflow-hidden border border-[#E1D8C6] aspect-[4/3] bg-white shadow-xs"
                >
                  <img
                    :src="imgSrc"
                    class="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- SECTION 2: EDITORIAL & METRICS MANAGEMENT -->
        <div
          class="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start"
        >
          <!-- LEFT: EDITORIAL COPY & ASSETS -->
          <section
            class="lg:col-span-7 rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-7 shadow-xs space-y-5"
          >
            <div
              class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4"
            >
              <div class="flex items-center space-x-2.5">
                <div
                  class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"
                >
                  <FileTextIcon class="h-4 w-4" />
                </div>
                <div>
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight"
                  >
                    Editorial Narrative
                  </h2>
                  <p class="text-[11px] text-slate-500">
                    Configure public titles, excerpts, and rich canvas story
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/20 hover:bg-indigo-500 transition active:scale-95 cursor-pointer"
                @click="openEditorModal"
              >
                <Edit3Icon class="h-3.5 w-3.5" />
                <span>Launch Block Canvas</span>
              </button>
            </div>

            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                  >
                    Eyebrow / Subtitle
                  </label>
                  <input
                    v-model="formData.aboutEyebrow"
                    type="text"
                    placeholder="OUR PURPOSE"
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-medium text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  />
                </div>

                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                  >
                    Main Heading Title
                  </label>
                  <input
                    v-model="formData.aboutHeading"
                    type="text"
                    placeholder="School of Excellence"
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label
                  class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                >
                  Card Summary Excerpt
                </label>
                <textarea
                  v-model="formData.aboutSchool"
                  rows="3"
                  placeholder="Detail your institution's affiliation, mission, legacy, and academic philosophy..."
                  class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-xs text-slate-700 leading-relaxed focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                ></textarea>
              </div>

              <!-- Cover Image Upload -->
              <div class="pt-2 border-t border-slate-100">
                <label
                  class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2"
                >
                  Featured Cover Photograph
                </label>
                <div class="flex items-center gap-4">
                  <div
                    class="relative group rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden bg-slate-50 h-24 w-32 sm:h-28 sm:w-36 flex flex-col items-center justify-center p-2 text-center shrink-0 hover:border-indigo-400 transition"
                  >
                    <img
                      v-if="
                        photoPreview ||
                        formData.aboutImage ||
                        formData.heroImage
                      "
                      :src="
                        photoPreview ||
                        formData.aboutImage ||
                        formData.heroImage
                      "
                      class="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                      v-else
                      class="space-y-1 text-slate-400"
                    >
                      <ImageIcon class="mx-auto h-5 w-5" />
                      <span
                        class="block text-[9px] font-bold uppercase tracking-wider"
                        >Upload</span
                      >
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      class="absolute inset-0 opacity-0 cursor-pointer z-10"
                      @change="handlePhotoSelect"
                    />

                    <button
                      v-if="photoPreview || formData.aboutImage"
                      type="button"
                      class="absolute top-1.5 right-1.5 z-20 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition"
                      @click.stop="removePhoto"
                    >
                      <XIcon class="h-3 w-3" />
                    </button>
                  </div>

                  <div class="text-xs text-slate-500 space-y-1">
                    <p class="font-bold text-slate-700 text-xs">
                      Primary 4:3 Banner Asset
                    </p>
                    <p class="text-[11px] text-slate-500 leading-relaxed">
                      Displays on the landing card and dedicated About hero with
                      your seal stamp.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Multi-Photo Gallery -->
              <div class="pt-4 border-t border-slate-100 space-y-3">
                <div class="flex items-center justify-between">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider"
                    >
                      Campus Life Gallery
                    </label>
                    <p class="text-[11px] text-slate-400">
                      Add high-resolution photos for the campus album
                    </p>
                  </div>
                  <label
                    class="inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-1.5 text-xs font-bold text-indigo-600 hover:bg-indigo-100 active:scale-95 transition cursor-pointer"
                  >
                    <PlusIcon class="h-3.5 w-3.5" />
                    <span>Upload Photos</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      class="hidden"
                      @change="handleGalleryFilesSelect"
                    />
                  </label>
                </div>

                <div
                  v-if="formData.images && formData.images.length > 0"
                  class="grid grid-cols-3 sm:grid-cols-4 gap-2.5 pt-1"
                >
                  <div
                    v-for="(imgSrc, idx) in formData.images"
                    :key="idx"
                    class="relative group rounded-2xl border border-slate-200 bg-slate-100 aspect-[4/3] overflow-hidden shadow-xs"
                  >
                    <img
                      :src="imgSrc"
                      class="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      class="absolute top-1.5 right-1.5 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition opacity-100 sm:opacity-0 sm:group-hover:opacity-100 z-10"
                      @click="removeGalleryImage(idx)"
                    >
                      <XIcon class="h-3 w-3" />
                    </button>
                  </div>
                </div>

                <div
                  v-else
                  class="p-6 text-center border-2 border-dashed border-slate-200/80 rounded-2xl bg-slate-50/50"
                >
                  <ImageIcon class="mx-auto h-6 w-6 text-slate-300 mb-1" />
                  <p class="text-xs text-slate-400">
                    No gallery images added yet.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- RIGHT: REPORT SHEET METRICS (STATS) -->
          <section
            class="lg:col-span-5 rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-7 shadow-xs space-y-4"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-4"
            >
              <div class="flex items-center space-x-2.5">
                <div
                  class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"
                >
                  <ListOrderedIcon class="h-4 w-4" />
                </div>
                <div>
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight"
                  >
                    Report Card Metrics
                  </h2>
                  <p class="text-[11px] text-slate-500">
                    Key statistics rendered in the report card
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-xl bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 active:scale-95 transition cursor-pointer"
                @click="addStat"
              >
                <PlusIcon class="h-3.5 w-3.5 text-indigo-600" />
                <span>Add</span>
              </button>
            </div>

            <!-- Stats List -->
            <div class="space-y-2.5">
              <div
                v-for="(st, index) in formData.stats"
                :key="st._id || index"
                class="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-2.5 sm:p-3 flex items-center gap-2"
              >
                <div class="w-28 sm:w-32">
                  <label
                    class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5"
                    >Metric Value</label
                  >
                  <input
                    v-model="st.number"
                    type="text"
                    placeholder="7+ acres"
                    class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-mono font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div class="flex-1 min-w-0">
                  <label
                    class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5"
                    >Label Title</label
                  >
                  <input
                    v-model="st.label"
                    type="text"
                    placeholder="Campus footprint"
                    class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <button
                  type="button"
                  class="mt-3.5 p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer active:scale-95 shrink-0"
                  @click="removeStat(index)"
                >
                  <Trash2Icon class="h-4 w-4" />
                </button>
              </div>

              <div
                v-if="formData.stats.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200/80 rounded-2xl bg-slate-50/50"
              >
                <p class="text-xs text-slate-400">No metrics configured.</p>
                <button
                  type="button"
                  class="mt-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                  @click="addStat"
                >
                  + Add First Metric
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- COMPACT STICKY BOTTOM ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-md pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2.5 py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0"
              >
                <span
                  class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate"
              >
                <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
                <span class="hidden sm:inline text-xs truncate"
                  >All changes saved</span
                >
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Changes"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- FULL-SCREEN POST EDITOR MODAL WITH GUTENBERG CANVAS -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md flex flex-col justify-between overflow-hidden"
    >
      <!-- TOP HEADER BAR -->
      <div
        class="h-14 bg-slate-900 border-b border-slate-800 px-4 flex items-center justify-between text-white flex-shrink-0"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center font-black text-sm shadow-md shadow-indigo-600/30"
          >
            A
          </div>
          <div>
            <h2 class="text-xs font-bold leading-tight">
              {{ formData.aboutHeading || "About Page Content" }}
            </h2>
            <p class="text-[10px] text-slate-400 font-mono">/about</p>
          </div>
        </div>

        <div class="flex items-center gap-2.5">
          <button
            type="button"
            class="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 transition cursor-pointer"
            @click="toggleSidebar"
          >
            <SidebarIcon class="h-3.5 w-3.5" />
            <span>{{
              isSidebarOpen ? "Hide Settings" : "Section Settings"
            }}</span>
          </button>

          <button
            type="button"
            class="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition active:scale-95 cursor-pointer shadow-md shadow-indigo-600/30"
            @click="closeModal"
          >
            Done & Apply
          </button>

          <button
            type="button"
            class="p-1.5 text-slate-400 hover:text-white rounded-xl transition cursor-pointer"
            @click="closeModal"
          >
            <XIcon class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- EDITOR BODY CONTAINER -->
      <div class="flex-1 flex overflow-hidden bg-slate-50">
        <!-- MOBILE LIGHT FORM EDITOR (< md) -->
        <div class="md:hidden flex-1 overflow-y-auto p-4 space-y-4 bg-white">
          <div
            class="rounded-xl bg-indigo-50/60 p-3 border border-indigo-100 text-xs text-indigo-800 flex items-center gap-2"
          >
            <AlertCircleIcon class="h-4 w-4 shrink-0 text-indigo-600" />
            <span
              >Mobile editor enabled. Switch to desktop for live block canvas
              editing.</span
            >
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Heading Title *</label
            >
            <input
              v-model="formData.aboutHeading"
              type="text"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Full Story HTML Content</label
            >
            <textarea
              v-model="formData.aboutContent"
              rows="8"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-mono text-slate-800 focus:border-indigo-500 focus:outline-none"
            ></textarea>
          </div>
        </div>

        <!-- DESKTOP GUTENBERG CANVAS (>= md) -->
        <div
          class="hidden md:flex flex-1 overflow-y-auto p-4 sm:p-8 md:p-12 justify-center"
        >
          <div
            class="w-full max-w-3xl bg-white border border-slate-200/90 shadow-md rounded-2xl p-6 sm:p-10 space-y-6 self-start"
          >
            <div>
              <input
                v-model="formData.aboutHeading"
                type="text"
                placeholder="Institutional Headline / Title..."
                class="w-full text-2xl sm:text-3xl font-black text-slate-900 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 outline-none transition py-2"
              />
            </div>

            <!-- TOOLBAR -->
            <div
              class="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-md text-slate-200 rounded-xl p-1.5 shadow-xl flex flex-wrap items-center gap-1 border border-slate-800"
            >
              <button
                type="button"
                @click="formatDoc('formatBlock', 'h2')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
              >
                H2
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'h3')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
              >
                H3
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'p')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
              >
                P
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <button
                type="button"
                @click="formatDoc('bold')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Bold"
              >
                <BoldIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('italic')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Italic"
              >
                <ItalicIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('underline')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Underline"
              >
                <span class="font-bold underline text-xs">U</span>
              </button>
              <button
                type="button"
                @click="formatDoc('strikeThrough')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Strikethrough"
              >
                <span class="font-bold line-through text-xs">S</span>
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <button
                type="button"
                @click="formatDoc('insertUnorderedList')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Bullet List"
              >
                <ListIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('insertOrderedList')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Ordered List"
              >
                <ListOrderedIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'blockquote')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Blockquote"
              >
                <QuoteIcon class="h-3.5 w-3.5" />
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <button
                type="button"
                @click="promptInsertLink"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Insert Link"
              >
                <LinkIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('insertHorizontalRule')"
                class="px-2 py-1 text-[10px] font-mono font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Divider"
              >
                ---
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <!-- In-Canvas Direct Images Upload -->
              <button
                type="button"
                class="px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 transition flex items-center gap-1.5 cursor-pointer active:scale-95 border border-amber-500/20"
                title="Insert One or Multiple Photos directly into Content"
                @click="triggerInEditorImageUpload"
              >
                <ImageIcon class="h-3.5 w-3.5" />
                <span class="text-[10px] font-bold uppercase tracking-wide"
                  >+ Add Photos</span
                >
              </button>

              <input
                ref="inEditorFileInput"
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="handleInEditorImagesSelected"
              />

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <button
                type="button"
                @click="formatDoc('undo')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Undo"
              >
                <UndoIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('redo')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer"
                title="Redo"
              >
                <RedoIcon class="h-3.5 w-3.5" />
              </button>
            </div>

            <!-- FLOATING IMAGE CONTEXT TOOLBAR -->
            <Transition name="fade">
              <div
                v-if="selectedImageEl"
                class="bg-slate-900 text-white rounded-xl p-2 shadow-2xl border border-slate-800 flex items-center gap-3 text-xs"
              >
                <span
                  class="text-[10px] font-bold text-amber-400 uppercase tracking-wider pl-1"
                  >Width:</span
                >
                <div
                  class="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5"
                >
                  <button
                    type="button"
                    @click="setSelectedImageWidth('25%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    25%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('50%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('75%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    75%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('100%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    100%
                  </button>
                </div>

                <div class="h-4 w-px bg-slate-800"></div>

                <span
                  class="text-[10px] font-bold text-amber-400 uppercase tracking-wider"
                  >Align:</span
                >
                <div
                  class="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5"
                >
                  <button
                    type="button"
                    @click="setSelectedImageAlign('left')"
                    class="p-1 rounded hover:bg-slate-700"
                    title="Align Left"
                  >
                    <AlignLeftIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageAlign('center')"
                    class="p-1 rounded hover:bg-slate-700"
                    title="Align Center"
                  >
                    <AlignCenterIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageAlign('right')"
                    class="p-1 rounded hover:bg-slate-700"
                    title="Align Right"
                  >
                    <AlignRightIcon class="h-3.5 w-3.5" />
                  </button>
                </div>

                <div class="h-4 w-px bg-slate-800"></div>

                <button
                  type="button"
                  @click="deleteSelectedImage"
                  class="p-1 text-rose-400 hover:text-rose-300 transition cursor-pointer ml-auto"
                  title="Remove Image"
                >
                  <Trash2Icon class="h-3.5 w-3.5" />
                </button>
              </div>
            </Transition>

            <!-- RICH CANVAS -->
            <div
              class="min-h-[380px] bg-slate-50/50 p-5 rounded-xl border border-slate-200/80 focus-within:border-indigo-600 transition"
            >
              <div
                ref="editableCanvas"
                contenteditable="true"
                class="prose prose-slate max-w-none focus:outline-none min-h-[340px] editor-body"
                @input="onEditorInput"
                @click="onCanvasClick"
                @keyup="saveSelection"
                @mouseup="saveSelection"
                @blur="saveSelection"
              ></div>
            </div>
          </div>
        </div>

        <!-- RIGHT SIDEBAR (Desktop Only) -->
        <div
          v-if="isSidebarOpen"
          class="hidden md:block w-80 bg-white border-l border-slate-200/80 overflow-y-auto p-5 space-y-6 flex-shrink-0"
        >
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">
            About Section Settings
          </h3>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Eyebrow / Subtitle</label
            >
            <input
              v-model="formData.aboutEyebrow"
              type="text"
              placeholder="OUR PURPOSE"
              class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Headline Title</label
            >
            <input
              v-model="formData.aboutHeading"
              type="text"
              placeholder="School of Excellence"
              class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Summary Description</label
            >
            <textarea
              v-model="formData.aboutSchool"
              rows="3"
              placeholder="Summary description..."
              class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset the About section, gallery, and Report
          Card stats back to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    reactive,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
  } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    FileText as FileTextIcon,
    Image as ImageIcon,
    ListOrdered as ListOrderedIcon,
    X as XIcon,
    Edit3 as Edit3Icon,
    Bold as BoldIcon,
    Italic as ItalicIcon,
    List as ListIcon,
    Quote as QuoteIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
    PanelRightClose as SidebarIcon,
    AlignLeft as AlignLeftIcon,
    AlignCenter as AlignCenterIcon,
    AlignRight as AlignRightIcon,
    Link as LinkIcon,
    Eye as EyeIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const isModalOpen = ref(false);
  const isSidebarOpen = ref(true);

  const editableCanvas = ref(null);
  const inEditorFileInput = ref(null);
  const selectedImageEl = ref(null);

  const photoFile = ref(null);
  const photoPreview = ref("");
  const initialStateJson = ref("");

  let savedSelectionRange = null;

  const formData = reactive({
    aboutEyebrow: "OUR PURPOSE",
    aboutHeading: "School of Excellence",
    aboutSchool: "",
    aboutContent:
      "<p>Welcome to our institutional story and educational vision...</p>",
    establishmentYear: "1985",
    admissionSession: "2026–27",
    aboutImage: "",
    heroImage: "",
    images: [],
    stats: [],
  });

  /* =========================================
  SELECTION PRESERVATION & RESTORATION
========================================= */
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (
        editableCanvas.value &&
        editableCanvas.value.contains(range.commonAncestorContainer)
      ) {
        savedSelectionRange = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRange) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRange);
    }
  };

  /* =========================================
  RICH TEXT EDITOR CONTROLS & SELECTION
========================================= */
  const formatDoc = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    if (editableCanvas.value) {
      formData.aboutContent = editableCanvas.value.innerHTML;
    }
    saveSelection();
  };

  const promptInsertLink = () => {
    const url = prompt("Enter Target URL (e.g. https://domain.com):");
    if (url) {
      formatDoc("createLink", url);
    }
  };

  const onEditorInput = () => {
    if (editableCanvas.value) {
      formData.aboutContent = editableCanvas.value.innerHTML;
    }
  };

  const onCanvasClick = (e) => {
    saveSelection();
    if (e.target && e.target.tagName === "IMG") {
      selectedImageEl.value = e.target;
    } else {
      selectedImageEl.value = null;
    }
  };

  const setSelectedImageWidth = (widthStr) => {
    if (selectedImageEl.value) {
      selectedImageEl.value.style.width = widthStr;
      onEditorInput();
    }
  };

  const setSelectedImageAlign = (alignStr) => {
    if (selectedImageEl.value) {
      if (alignStr === "left") {
        selectedImageEl.value.style.marginLeft = "0";
        selectedImageEl.value.style.marginRight = "auto";
      } else if (alignStr === "right") {
        selectedImageEl.value.style.marginLeft = "auto";
        selectedImageEl.value.style.marginRight = "0";
      } else {
        selectedImageEl.value.style.marginLeft = "auto";
        selectedImageEl.value.style.marginRight = "auto";
      }
      onEditorInput();
    }
  };

  const deleteSelectedImage = () => {
    if (selectedImageEl.value) {
      selectedImageEl.value.remove();
      selectedImageEl.value = null;
      onEditorInput();
    }
  };

  const syncEditorDOM = () => {
    selectedImageEl.value = null;
    savedSelectionRange = null;
    nextTick(() => {
      if (editableCanvas.value) {
        editableCanvas.value.innerHTML = formData.aboutContent || "<p></p>";
      }
    });
  };

  watch(isModalOpen, (isOpen) => {
    if (isOpen) {
      syncEditorDOM();
    }
  });

  /* IN-CANVAS IMAGE UPLOAD & NODE INSERTION */
  const triggerInEditorImageUpload = () => {
    saveSelection();
    if (inEditorFileInput.value) {
      inEditorFileInput.value.click();
    }
  };

  const insertNodeAtCaret = (node) => {
    restoreSelection();
    const sel = window.getSelection();

    if (
      sel &&
      sel.rangeCount > 0 &&
      editableCanvas.value &&
      editableCanvas.value.contains(sel.anchorNode)
    ) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(node);

      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (editableCanvas.value) {
      editableCanvas.value.appendChild(node);
    }

    onEditorInput();
    saveSelection();
  };

  const handleInEditorImagesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Src = event.target?.result;
        if (base64Src) {
          const img = document.createElement("img");
          img.src = base64Src;
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.borderRadius = "0.75rem";
          img.style.margin = "1rem 0";
          img.style.border = "1px solid #e2e8f0";
          img.style.display = "block";
          img.style.cursor = "pointer";

          insertNodeAtCaret(img);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };

  const openEditorModal = () => {
    isModalOpen.value = true;
  };

  const closeModal = () => {
    if (editableCanvas.value) {
      formData.aboutContent = editableCanvas.value.innerHTML;
    }
    isModalOpen.value = false;
  };

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  function getCleanState() {
    return {
      aboutEyebrow: formData.aboutEyebrow || "",
      aboutHeading: formData.aboutHeading || "",
      aboutSchool: formData.aboutSchool || "",
      aboutContent: formData.aboutContent || "",
      aboutImage:
        typeof formData.aboutImage === "string" ? formData.aboutImage : "",
      hasNewPhoto: photoFile.value instanceof File,
      photoFileName:
        photoFile.value instanceof File ? photoFile.value.name : "",
      images: [...formData.images],
      stats: formData.stats.map((st) => ({
        _id: st._id,
        number: st.number || "",
        label: st.label || "",
      })),
    };
  }

  const hasUnsavedChanges = computed(() => {
    return JSON.stringify(getCleanState()) !== initialStateJson.value;
  });

  function snapshotState() {
    initialStateJson.value = JSON.stringify(getCleanState());
  }

  function handlePhotoSelect(event) {
    const file = event.target.files[0];
    if (!file) return;
    photoFile.value = file;
    photoPreview.value = URL.createObjectURL(file);
  }

  function removePhoto() {
    photoFile.value = null;
    photoPreview.value = "";
    formData.aboutImage = "";
  }

  function handleGalleryFilesSelect(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Src = e.target?.result;
        if (base64Src) {
          formData.images.push(base64Src);
        }
      };
      reader.readAsDataURL(file);
    });
    event.target.value = "";
  }

  function removeGalleryImage(index) {
    formData.images.splice(index, 1);
  }

  function addStat() {
    formData.stats.push({
      number: "10+",
      label: "New Metric",
    });
  }

  function removeStat(index) {
    formData.stats.splice(index, 1);
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details =
        store.state.website?.details || store.state.websiteDetails || {};
      const aboutData = details.aboutSection || {};

      formData.aboutEyebrow =
        aboutData.eyebrow || details.aboutEyebrow || "OUR PURPOSE";
      formData.aboutHeading =
        aboutData.title || details.aboutHeading || "School of Excellence";
      formData.aboutSchool =
        aboutData.description ||
        details.aboutSchool ||
        details.description ||
        "";
      formData.aboutContent =
        aboutData.content ||
        "<p>Welcome to our institutional story and educational vision...</p>";
      formData.establishmentYear =
        details.establishmentYear || details.foundedYear || "1985";
      formData.admissionSession = details.admissionSession || "2026–27";
      formData.aboutImage =
        aboutData.image || details.aboutImage || details.heroImage || "";
      formData.heroImage = details.heroImage || "";

      if (Array.isArray(aboutData.images) && aboutData.images.length > 0) {
        formData.images = [...aboutData.images];
      } else if (Array.isArray(details.gallery) && details.gallery.length > 0) {
        formData.images = details.gallery
          .map((g) => (typeof g === "string" ? g : g.image || ""))
          .filter(Boolean);
      } else {
        formData.images = [];
      }

      if (Array.isArray(details.stats) && details.stats.length > 0) {
        formData.stats = details.stats.map((st) => ({
          _id: st._id,
          number: st.number || "",
          label: st.label || "",
        }));
      } else {
        formData.stats = [
          { number: "7+ acres", label: "Campus footprint" },
          { number: "30+", label: "Sports & activities" },
          { number: "10+", label: "Student clubs" },
        ];
      }

      photoFile.value = null;
      photoPreview.value = "";
      snapshotState();
    } catch (err) {
      showToast("Failed to load About section data", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      if (editableCanvas.value) {
        formData.aboutContent = editableCanvas.value.innerHTML;
      }

      const payload = {
        aboutEyebrow: formData.aboutEyebrow,
        aboutHeading: formData.aboutHeading,
        aboutSchool: formData.aboutSchool,
        aboutImage: photoFile.value || formData.aboutImage || "",
        aboutSection: {
          title: formData.aboutHeading,
          eyebrow: formData.aboutEyebrow,
          description: formData.aboutSchool,
          content: formData.aboutContent,
          image:
            typeof formData.aboutImage === "string" ? formData.aboutImage : "",
          imageFile: photoFile.value || null,
          images: [...formData.images],
        },
        stats: formData.stats.map((st) => ({
          _id: st._id,
          number: st.number || "",
          label: st.label || "",
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast("About section and gallery saved successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving About details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("About section reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .editor-body {
    outline: none;
  }
  .editor-body :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 0.75rem;
    margin: 1rem 0;
    border: 1px solid #e2e8f0;
    display: block;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .editor-body :deep(img:hover) {
    outline: 3px solid #6366f1;
    outline-offset: 2px;
  }
  .editor-body :deep(h1) {
    font-size: 1.875rem;
    font-weight: 900;
    color: #0f172a;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(h2) {
    font-size: 1.5rem;
    font-weight: 800;
    color: #0f172a;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }
  .editor-body :deep(h3) {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .editor-body :deep(p) {
    margin-bottom: 0.75rem;
    color: #334155;
    line-height: 1.6;
  }
  .editor-body :deep(ul) {
    list-style-type: disc;
    padding-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(ol) {
    list-style-type: decimal;
    padding-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(blockquote) {
    border-left: 4px solid #6366f1;
    padding-left: 1rem;
    font-style: italic;
    color: #475569;
    margin: 1rem 0;
  }
  .editor-body :deep(a) {
    color: #4f46e5;
    text-decoration: underline;
    font-weight: 600;
  }

  .toast-enter-active,
  .toast-leave-active,
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
</style>
